// /app/frontend/src/pages/admin/AdminRoomPromptsPage.jsx
// Admin Room Prompts - Manage prompts for a specific room

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  RotateCcw,
  Eye,
  Check,
  Copy,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from "../../api/client";
import { AuthContext } from '../../App';
import AdminHelp from '../../components/admin/AdminHelp';

const API = window.location.origin;

export default function AdminRoomPromptsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomKey = searchParams.get('room') || '';

  const [loading, setLoading] = useState(true);
  const [promptSets, setPromptSets] = useState([]);
  const [activeSetId, setActiveSetId] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [preview, setPreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Edit states
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [editForm, setEditForm] = useState({ prompt_text: '', theme_badge: '' });
  
  // Add new prompt state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPrompt, setNewPrompt] = useState({ prompt_text: '', theme_badge: '' });
  
  // Duplicate set state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateSourceId, setDuplicateSourceId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch prompt sets for room
  const fetchPromptSets = useCallback(async () => {
    if (!roomKey) return;
    try {
      const res = await apiClient.get(`/admin/content/prompt-sets`, {
        headers,
        params: { room_key: roomKey }
      });
      setPromptSets(res.data);
      
      // Find active set
      const active = res.data.find(s => s.is_active);
      if (active) {
        setActiveSetId(active.id);
      }
    } catch (err) {
      console.error('Failed to fetch prompt sets:', err);
      toast.error('Failed to load topic sets');
    }
  }, [token, roomKey]);

  // Fetch prompts for active set
  const fetchPrompts = useCallback(async () => {
    if (!activeSetId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/content/prompts`, {
        headers,
        params: { set_id: activeSetId }
      });
      setPrompts(res.data);
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  }, [token, activeSetId]);

  // Fetch preview
  const fetchPreview = useCallback(async () => {
    if (!roomKey) return;
    try {
      const res = await apiClient.get(`/admin/content/prompts/preview`, {
        headers,
        params: { room_key: roomKey, days: 14 }
      });
      setPreview(res.data);
    } catch (err) {
      console.error('Failed to fetch preview:', err);
    }
  }, [token, roomKey]);

  useEffect(() => {
    fetchPromptSets();
  }, [fetchPromptSets]);

  useEffect(() => {
    if (activeSetId) {
      fetchPrompts();
      fetchPreview();
    }
  }, [activeSetId, fetchPrompts, fetchPreview]);

  // Activate a prompt set
  const handleActivateSet = async (setId) => {
    try {
      await apiClient.post(`/admin/content/prompt-sets/${setId}/activate`, {}, { headers });
      toast.success('Prompt set activated');
      setActiveSetId(setId);
      fetchPromptSets();
    } catch (err) {
      toast.error('Failed to activate set');
    }
  };

  // Duplicate prompt set
  const handleDuplicateSet = async () => {
    if (!duplicateSourceId || !duplicateName.trim()) return;
    try {
      const res = await apiClient.post(`/admin/content/prompt-sets/${duplicateSourceId}/duplicate`,
        {},
        { headers, params: { new_name: duplicateName } }
      );
      toast.success(`Created "${res.data.name}"`);
      setShowDuplicateModal(false);
      setDuplicateName('');
      fetchPromptSets();
    } catch (err) {
      toast.error('Failed to duplicate set');
    }
  };

  // Start editing a prompt
  const handleEditStart = (prompt) => {
    setEditingPromptId(prompt.id);
    setEditForm({
      prompt_text: prompt.prompt_text,
      theme_badge: prompt.theme_badge || ''
    });
  };

  // Save prompt edit
  const handleEditSave = async (promptId) => {
    try {
      await apiClient.patch(`/admin/content/prompts/${promptId}`,
        {
          prompt_text: editForm.prompt_text,
          theme_badge: editForm.theme_badge || null
        },
        { headers }
      );
      toast.success('Topic updated');
      setEditingPromptId(null);
      fetchPrompts();
      fetchPreview();
    } catch (err) {
      toast.error('Failed to update topic');
    }
  };

  // Toggle prompt enabled
  const handleToggleEnabled = async (prompt) => {
    try {
      await apiClient.patch(`/admin/content/prompts/${prompt.id}`,
        { is_enabled: !prompt.is_enabled },
        { headers }
      );
      toast.success(`Topic ${prompt.is_enabled ? 'disabled' : 'enabled'}`);
      fetchPrompts();
    } catch (err) {
      toast.error('Failed to toggle topic');
    }
  };

  // Delete prompt
  const handleDelete = async (promptId) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await apiClient.delete(`/admin/content/prompts/${promptId}`, { headers });
      toast.success('Topic deleted');
      fetchPrompts();
    } catch (err) {
      toast.error('Failed to delete topic');
    }
  };

  // Reset to default
  const handleResetToDefault = async (promptId) => {
    try {
      const res = await apiClient.post(`/admin/content/prompts/${promptId}/reset-to-default`,
        {},
        { headers }
      );
      toast.success('Topic reset to default');
      fetchPrompts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset topic');
    }
  };

  // Add new prompt
  const handleAddPrompt = async () => {
    if (!newPrompt.prompt_text.trim()) return;
    try {
      await apiClient.post(`/admin/content/prompts`,
        {
          prompt_text: newPrompt.prompt_text,
          theme_badge: newPrompt.theme_badge || null
        },
        { headers, params: { set_id: activeSetId } }
      );
      toast.success('Topic added');
      setShowAddForm(false);
      setNewPrompt({ prompt_text: '', theme_badge: '' });
      fetchPrompts();
    } catch (err) {
      toast.error('Failed to add topic');
    }
  };

  const activeSet = promptSets.find(s => s.id === activeSetId);

  if (!roomKey) {
    return (
      <div className="min-h-screen surface-page p-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gray-500">No room selected. Go back and select a room.</p>
          <button
            onClick={() => navigate('/admin/rooms')}
            className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg"
          >
            Back to Rooms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen surface-page p-4 md:p-6" data-testid="admin-room-prompts-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/admin/rooms')}
            className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {roomKey} Topics
              </h1>
              <AdminHelp helpKey="room_topics" />
            </div>
            {activeSet && (
              <p className="text-sm text-gray-500">
                Active: {activeSet.name} • {activeSet.cycle_length}-day cycle
              </p>
            )}
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition ${
              showPreview
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>

        {/* Prompt Sets Selector */}
        <div className="mb-4 flex flex-wrap gap-2">
          {promptSets.map((set) => (
            <button
              key={set.id}
              onClick={() => setActiveSetId(set.id)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition ${
                set.id === activeSetId
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700'
              }`}
            >
              {set.name}
              {set.is_active && <Check className="w-3 h-3" />}
            </button>
          ))}
          <button
            onClick={() => {
              setDuplicateSourceId(activeSetId);
              setDuplicateName(`${activeSet?.name || 'Set'} Copy`);
              setShowDuplicateModal(true);
            }}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400"
          >
            <Copy className="w-3 h-3" />
            Duplicate
          </button>
        </div>

        {/* Activate Button */}
        {activeSet && !activeSet.is_active && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-center justify-between">
            <span className="text-sm text-yellow-800 dark:text-yellow-300">
              This set is not active. Activate to use these topics.
            </span>
            <button
              onClick={() => handleActivateSet(activeSetId)}
              className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
            >
              Activate
            </button>
          </div>
        )}

        {/* Preview Panel */}
        {showPreview && (
          <div className="mb-6 surface-card rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-500" />
              Next 14 Days Preview
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {preview.map((day, idx) => (
                <div
                  key={idx}
                  className={`p-2 rounded-lg text-sm ${
                    day.has_prompt
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : 'bg-red-50 dark:bg-red-900/20'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {day.date}
                    </span>
                    <span className="text-xs text-gray-500">Day {day.day_index}</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                    {day.prompt_text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Topic Button */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition"
            data-testid="add-prompt-btn"
          >
            <Plus className="w-4 h-4" />
            Add Topic
          </button>
          <button
            onClick={() => { fetchPrompts(); fetchPreview(); }}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Add Topic Form */}
        {showAddForm && (
          <div className="mb-4 surface-card rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">New Topic</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Topic Text</label>
                <textarea
                  value={newPrompt.prompt_text}
                  onChange={(e) => setNewPrompt({ ...newPrompt, prompt_text: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                  rows={3}
                  placeholder="What brings you joy today?"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-1">Theme Badge (optional)</label>
                <input
                  type="text"
                  value={newPrompt.theme_badge}
                  onChange={(e) => setNewPrompt({ ...newPrompt, theme_badge: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                  placeholder="🌟 Joy"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddPrompt}
                  disabled={!newPrompt.prompt_text.trim()}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
                >
                  Add Topic
                </button>
                <button
                  onClick={() => { setShowAddForm(false); setNewPrompt({ prompt_text: '', theme_badge: '' }); }}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Topics List */}
        <div className="space-y-2">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading topics...
            </div>
          ) : prompts.length === 0 ? (
            <div className="p-8 text-center surface-card rounded-xl text-gray-500">
              No topics in this set. Add topics or seed defaults.
            </div>
          ) : (
            prompts.map((prompt) => {
              const isEditing = editingPromptId === prompt.id;
              
              return (
                <div
                  key={prompt.id}
                  className={`surface-card rounded-xl p-3 shadow-sm ${
                    !prompt.is_enabled ? 'opacity-50' : ''
                  }`}
                  data-testid={`prompt-${prompt.day_index}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Day Index */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <span className="text-sm font-bold text-purple-700 dark:text-purple-300">
                        {prompt.day_index}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editForm.prompt_text}
                            onChange={(e) => setEditForm({ ...editForm, prompt_text: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                            rows={2}
                          />
                          <input
                            type="text"
                            value={editForm.theme_badge}
                            onChange={(e) => setEditForm({ ...editForm, theme_badge: e.target.value })}
                            className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                            placeholder="Theme badge (optional)"
                          />
                        </div>
                      ) : (
                        <>
                          {prompt.theme_badge && (
                            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 mb-1">
                              {prompt.theme_badge}
                            </span>
                          )}
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {prompt.prompt_text}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleEditSave(prompt.id)}
                            className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 hover:bg-green-200"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingPromptId(null)}
                            className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditStart(prompt)}
                            className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 hover:bg-gray-200"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleEnabled(prompt)}
                            className={`p-1.5 rounded-lg ${
                              prompt.is_enabled
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600'
                                : 'bg-gray-100 dark:bg-slate-800 text-gray-400'
                            }`}
                            title={prompt.is_enabled ? 'Disable' : 'Enable'}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleResetToDefault(prompt.id)}
                            className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-500 hover:bg-gray-200"
                            title="Reset to default"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(prompt.id)}
                            className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Duplicate Modal */}
        {showDuplicateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Duplicate Prompt Set
              </h3>
              <input
                type="text"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 mb-3"
                placeholder="New set name"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDuplicateSet}
                  disabled={!duplicateName.trim()}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50"
                >
                  Duplicate
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
